import { ROLE_ID, sourcesFord } from '../../config';

export class AuthClass {
  constructor(messenger, api) {
    this.messenger = messenger;
    this.messenger.addListener('refreshToken', () => this.refreshToken());
    this.api = api;
    this.idToken = undefined;
    this.users = undefined;
    this.apps = undefined;
  }

  async setToken(token) {
    this.idToken = token;
    await this.getCurrentUser();
    this.sendTokenToSw();
  }

  async setApp(apps) {
    this.apps = { office_apps: apps };
  }

  async getCurrentUser() {
    if (!this.idToken) throw new Error('error ');
    const { data } = await this.api('getUserInfos');

    let userKey = '';
    const { PROVIDER, COUNTRY, CRM } = data?.tags || {};
    const autobizIdSplit = data?.autobiz_id?.split('|');

    if (PROVIDER === 'STLA') {
      userKey = `${COUNTRY}${autobizIdSplit?.[1]}`;
    } else if (PROVIDER === 'AZ' || PROVIDER === 'SMART') {
      userKey = `${COUNTRY}${CRM}`;
    }
    const carcheckUser = await this.api('getCarcheckUser', { userKey });
    const requiredKeys = [
      'roleId',
      'groupId',
      'sourceIds',
      'journeysIds',
      'contractJourneyId',
      'marketAnalysis',
      'brandIds',
    ];
    for (let i = 0; i < requiredKeys.length; i++) {
      const key = requiredKeys[i];
      if (carcheckUser.data[key] === null || carcheckUser.data[key] === undefined) {
        console.log(`Value for "${key}" is null or undefined`);
        throw new Error('user_have_no_right_to_use_app');
      }
    }
    this.users = { ...data, ...carcheckUser.data };
    window.dispatchEvent(
      new StorageEvent('storage', {
        key: 'carcheck.staticContent',
      }),
    );
    return this.users;
  }

  async refreshToken() {
    if (!this.users) await this.getCurrentUser();
    this.sendTokenToSw();
  }

  token() {
    return this.idToken;
  }

  sendTokenToSw() {
    try {
      this.messenger.sendMessage('tokenData', this.idToken);
      this.messenger.sendMessage('currentUser', { ...this.currentUser(), client: this.client(), role: this.role() });
    } catch (e) {
      console.log('sw not available'); // eslint-disable-line no-console
    }
  }

  currentUser() {
    if (!this.users) this.getCurrentUser();
    return this.users;
  }

  currentUserApps() {
    if (!this.token() || !this.apps) return [];
    let appLists = this.apps.office_apps;
    appLists = appLists.filter((app) => app.name !== 'autobizCarcheck');
    return appLists;
  }

  hasRules(rulesAuthorized) {
    const rules = this.currentUser().orgRoles;
    const intersection = rulesAuthorized.filter((x) => rules.includes(x));
    return intersection.length >= 1;
  }

  client() {
    const { source } = this.currentUser();
    if (!source) throw new Error('user_have_no_right_to_use_app');
    if (source.id !== undefined) this.refreshToken();
    source.forEach((s, i) => {
      const label = s.toLowerCase();
      source[i] = label;
      if (label === 'easyreprise') source[i] = 'carcheck';
      if (label === 'mobile.de') source[i] = 'mobile';
    });
    return source;
  }

  roleId() {
    return this.users?.roleId;
  }

  enableCoverageRequest() {
    return this.users?.enableCoverageRequest;
  }

  groupId() {
    return this.users.groupId;
  }

  role() {
    return ROLE_ID[this.roleId()];
  }

  userId() {
    return parseInt(this.users?.tags?.CRM, 10);
  }

  userFullId() {
    const country = this.users?.tags?.COUNTRY;
    const crm = this.users?.tags?.CRM;
    if (country && crm) {
      return `${country}_${crm}`;
    }

    return null;
  }

  sourceId() {
    return this.users?.sourceIds[0];
  }

  fullName() {
    return `${this.users.firstname || ''} ${this.users.lastname || ''}`;
  }

  firstName() {
    return this.users?.firstname || '';
  }

  lastName() {
    return this.users?.lastname || '';
  }

  email() {
    return this.users?.org_email || '';
  }

  country() {
    return this.users?.tags?.COUNTRY;
  }

  provider() {
    return this.users?.tags?.PROVIDER;
  }

  concessionIds() {
    return this.users?.concessionIds;
  }

  journeys() {
    const { journeysIds } = this.users || {};
    if ([null, undefined].includes(journeysIds)) {
      this.refreshToken();
    }
    return journeysIds;
  }

  id() {
    return this.users?.id;
  }

  preferredLanguage() {
    if (['FR', 'ES', 'IT', 'DE', 'NL', 'UK', 'PT'].includes(this.users?.preferred_language?.toUpperCase()))
      return this.users?.preferred_language;
    return 'en';
  }

  async updatePreferredLanguage(languageId, language) {
    const preferredLanguage = { preferred_language: `/api/languages/${languageId}` };
    this.users.preferred_language = language;
    try {
      const { data } = await this.api(
        'updatePreferredLanguage',
        { userId: this.id() },
        null,
        JSON.stringify(preferredLanguage),
      );
      if (data && data.preferred_language?.language_iso2) {
        this.users.preferred_language = data.preferred_language.language_iso2;
        return this.users.preferred_language;
      }
      return data;
    } catch (e) {
      return e;
    }
  }

  contract() {
    const { contractJourneyId } = this.users || {};
    if ([undefined].includes(contractJourneyId)) {
      this.refreshToken();
    }
    return contractJourneyId;
  }

  sourceIds() {
    const { sourceIds } = this.users;
    if ([null, undefined].includes(sourceIds)) {
      this.refreshToken();
    }
    return sourceIds;
  }

  isAllowedToAccessPricing() {
    const roleId = this.roleId();
    return [2, 3, 4, 5, 6].includes(roleId) || (roleId === 1 && this.enableCoverageRequest());
  }

  isAllowedToAccessTrade() {
    return [2, 3, 4, 5, 6].includes(this.roleId());
  }

  isAllowedToAccessMarket() {
    const { marketAnalysis } = this.currentUser();
    if (this.country() === 'NL') return false;
    if ([null, undefined].includes(marketAnalysis)) {
      this.refreshToken();
    }
    return marketAnalysis;
  }

  isAllowedToAccessFord() {
    let isAllowed = false;
    try {
      this.client().forEach((client) => {
        if (sourcesFord.includes(client)) isAllowed = true;
      });
    } catch {
      isAllowed = false;
    }
    return isAllowed;
  }

  isShowCtaContreInsp() {
    if (this.country() === 'ES' || this.country() === 'PT') return false;
    return true;
  }

  isAllowedToCtaExport() {
    return [2, 5].includes(this.roleId());
  }

  isAllowedToSeeMerchant() {
    const { hideMerchantInfo } = this.users;
    return hideMerchantInfo;
  }

  isAllowedToVinDecoding() {
    const { enableVinDecoding } = this.users;
    return enableVinDecoding;
  }

  isOnlyNrmUser() {
    if (!this.users) return null;
    const { journeysIds } = this.users;
    return journeysIds.length === 1 && journeysIds.includes(2);
  }

  isNrmUser() {
    const { journeysIds } = this.users;
    return journeysIds.includes(2);
  }

  isOnlyCarcheckUser() {
    const { journeysIds } = this.users;
    return journeysIds.length === 1 && journeysIds.includes(1);
  }

  isCarcheckUser() {
    const { journeysIds } = this.users;
    return journeysIds.includes(1);
  }

  isSmartContract() {
    const contractJourneyId = this.contract();
    return contractJourneyId === 2;
  }

  isSolContract() {
    const contractJourneyId = this.contract();
    return contractJourneyId === 3;
  }

  brandIds() {
    const { brandIds } = this.users;
    if ([null, undefined].includes(brandIds)) {
      this.refreshToken();
    }
    return brandIds;
  }

  roleViewerAccess() {
    return [7].includes(this.roleId());
  }

  isAllowedToChangeBuyer() {
    return [2, 3, 4, 5, 6].includes(this.roleId());
  }

  notAllowedToSeeAllConcessionsFiles() {
    return this.role() === 'buyer' && this.users?.seeAllConcessionsFiles === false;
  }
}
